import { ComputedRef, Ref, ref } from "vue";
import type { DSBaseListOption } from "@/src/design_system/form_elements/option_list/types";
import { DSOptionValue } from "@/src/design_system/form_elements/shared/types/option";
import { selectNextMatchingOption } from "@/src/design_system/form_elements/shared/utils/keyboardNavigation";

interface UseSelectKeyboardNavigationArgs {
  baseOptions: ComputedRef<DSBaseListOption[]>;
  selectedValues: Ref<DSOptionValue[]>;
  updateSelection: (value: DSOptionValue) => void;
}

interface UseSelectKeyboardNavigation {
  onAlphabeticKey: (key: string) => void;
}

export const useSelectKeyboardNavigation = ({
  baseOptions,
  selectedValues,
  updateSelection,
}: UseSelectKeyboardNavigationArgs): UseSelectKeyboardNavigation => {
  const lastPressedKey = ref("");

  const getOptionLabel = (option: DSBaseListOption): string => option.label ?? option.value ?? "";

  const optionStartsWith = (option: DSBaseListOption, char: string): boolean =>
    getOptionLabel(option).toLowerCase().startsWith(char);

  const findMatchingOptions = (char: string): DSBaseListOption[] =>
    baseOptions.value.filter(option => optionStartsWith(option, char));

  const onAlphabeticKey = (key: string): void => {
    const searchChar = key.toLowerCase();
    const matchingOptions = findMatchingOptions(searchChar);

    if (matchingOptions.length === 0) return;

    const currentValue = selectedValues.value[0];
    const currentOption = baseOptions.value.find(opt => opt.value === currentValue);
    const shouldCycle =
      lastPressedKey.value === searchChar && currentOption !== undefined && optionStartsWith(currentOption, searchChar);

    const currentIndex = matchingOptions.findIndex(opt => opt.value === currentValue);
    const nextOption = selectNextMatchingOption(matchingOptions, currentIndex, shouldCycle);

    updateSelection(nextOption.value);
    lastPressedKey.value = searchChar;
  };

  return { onAlphabeticKey };
};
